import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Check, FileSearch, Home, MessageCircle, PhoneCall } from "lucide-react";
import { useSEO } from "../lib/seo";
import { trackEvent } from "../lib/api";
import { COMPANY } from "../lib/constants";
import { COMPARE_DONE_KEY } from "../components/compare/compareOptions";
import { COMPARE_PATH, COMPARE_PROMISES } from "../data/compareContent";
import { RequestNumber } from "./OfferteBedankt";

const STEPS = [
  { icon: Check, title: "Offertevergelijking ontvangen", text: "Uw gegevens en documenten zijn veilig ontvangen en opgeslagen." },
  { icon: FileSearch, title: "Persoonlijke beoordeling", text: "Wij bekijken uw bestaande offerte, de productspecificaties en de gewenste werkzaamheden." },
  { icon: PhoneCall, title: "Persoonlijk contact", text: "Wij nemen zo spoedig mogelijk persoonlijk contact met u op om de mogelijkheden te bespreken." },
];

const readDone = () => {
  try { return JSON.parse(sessionStorage.getItem(COMPARE_DONE_KEY) || "null"); } catch { return null; }
};

export default function OfferteVergelijkenBedankt() {
  const [done] = useState(readDone);
  useSEO({ title: "Bedankt voor uw offertevergelijking | Maas Kozijn", description: "Uw offertevergelijking bij Maas Kozijn is ontvangen.", path: `${COMPARE_PATH}/bedankt`, noindex: true });

  useEffect(() => {
    const cur = readDone();
    if (!cur || cur.tracked) return;
    sessionStorage.setItem(COMPARE_DONE_KEY, JSON.stringify({ ...cur, tracked: true }));
    trackEvent("generate_lead", { lead_type: "quote_comparison", product_category: cur.product_category, currency: "EUR", value: 1 });
  }, []);

  if (!done?.lead_number) return <Navigate to={COMPARE_PATH} replace />;
  const waText = `Goedendag Maas Kozijn, ik heb zojuist mijn offerte ter vergelijking gestuurd (aanvraagnummer ${done.lead_number}). Ik heb nog een vraag.`;
  const waLink = `https://api.whatsapp.com/send/?phone=${COMPANY.whatsappDigits}&text=${encodeURIComponent(waText)}`;

  return (
    <section className="bg-slate-50" data-testid="compare-thanks-page">
      <div className="container-tight max-w-4xl py-10 sm:py-16">
        <div className="reveal rounded-3xl border border-slate-200 bg-white p-6 text-center sm:p-10" style={{ boxShadow: "var(--shadow-2)" }}>
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 ring-8 ring-emerald-50/60">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-600 text-white"><Check className="h-6 w-6" strokeWidth={3} aria-hidden="true" /></span>
          </span>
          <h1 className="mt-6 text-[clamp(1.6rem,2.2vw+1rem,2.4rem)] font-bold leading-tight text-ink" data-testid="compare-thanks-title">Bedankt! Wij hebben uw offertevergelijking ontvangen.</h1>
          <p className="mx-auto mt-4 max-w-2xl text-[16px] leading-relaxed text-slate-600 sm:text-[17px]">
            Wij beoordelen de aangeleverde informatie persoonlijk en nemen zo spoedig mogelijk contact met u op.
          </p>
          <RequestNumber number={done.lead_number} />
          <p className="mt-3 text-[13px] text-slate-500">Vermeld dit nummer als u contact met ons opneemt over uw aanvraag.</p>
          <p className="mt-5 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[13.5px] font-medium text-slate-600">{COMPARE_PROMISES.join(" • ")}</p>
        </div>
        <h2 className="mt-12 text-[20px] font-bold text-ink sm:text-[22px]">Wat gebeurt er nu?</h2>
        <ol className="mt-6 grid gap-4 md:grid-cols-3" data-testid="compare-thanks-steps">
          {STEPS.map(({ icon: Icon, title, text }, i) => (
            <li key={title} className={`reveal rounded-2xl border p-5 ${i === 0 ? "border-maas/30 bg-maas-soft/60" : "border-slate-200 bg-white"}`} style={{ animationDelay: `${120 + i * 90}ms` }}>
              <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${i === 0 ? "bg-maas text-white" : "bg-slate-100 text-ink"}`}><Icon className="h-5 w-5" aria-hidden="true" /></span>
              <h3 className="mt-4 text-[16.5px] font-bold text-ink">{title}</h3>
              <p className="mt-1.5 text-[14.5px] leading-relaxed text-slate-600">{text}</p>
            </li>
          ))}
        </ol>
        <div className="mt-10 flex flex-col items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6 text-center sm:p-8">
          <p className="text-[17px] font-semibold text-ink">Wilt u nog iets aanvullen of heeft u een vraag?</p>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <a href={waLink} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("click_whatsapp", { page_type: "compare_thank_you" })}
              className="btn min-h-[52px] bg-[#1F8F4E] px-6 text-white hover:bg-[#18733F]" data-testid="compare-thanks-whatsapp">
              <MessageCircle className="h-5 w-5" aria-hidden="true" /> Stel uw vraag via WhatsApp
            </a>
            <Link to="/" className="btn-secondary min-h-[52px] px-6" data-testid="compare-thanks-home"><Home className="h-5 w-5" aria-hidden="true" /> Terug naar de homepage</Link>
          </div>
        </div>
      </div>
    </section>
  );
}
